import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Activity, MapPin, TrendingUp, Info, ArrowUpRight, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';
import { reportError } from '../lib/errorReporter';

function fmtPct(v, d = 1) { if (v == null) return '—'; return `${Number(v).toFixed(d)}%`; }
function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function Stat({ value, label, accent, sub }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-[#5A6660]">{sub}</div>}
    </div>
  );
}

function LineChart({ trend, accessor = 'ma_beneficiaries', color = '#4A7C5E', label }) {
  const data = (trend || []).filter(d => d[accessor] != null);
  if (data.length < 2) return <div className="text-[12px] text-[#A8B0AA]">No multi-year data</div>;
  const w = 520, h = 180, pad = 28;
  const min = Math.min(...data.map(d => d[accessor]));
  const max = Math.max(...data.map(d => d[accessor]));
  const range = max - min || 1;
  const xFor = (i) => pad + (i / (data.length - 1)) * (w - pad * 2);
  const yFor = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
  const pts = data.map((d, i) => `${xFor(i)},${yFor(d[accessor])}`).join(' ');
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#4A7C5E] mb-2">{label}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-44">
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#E5DCC4" />
        <polyline fill="none" stroke={color} strokeWidth="2.5" points={pts} />
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={xFor(i)} cy={yFor(d[accessor])} r="3" fill={color} />
            <text x={xFor(i)} y={h - pad + 14} textAnchor="middle" style={{ fontSize: '10px', fill: '#7A857F' }}>{d.year}</text>
          </g>
        ))}
        <text x={pad} y={pad - 6} style={{ fontSize: '10px', fill: '#A8B0AA' }}>{typeof max === 'number' && max > 1000 ? fmtInt(max) : max}</text>
        <text x={pad} y={h - pad + 28} style={{ fontSize: '10px', fill: '#A8B0AA' }}>{typeof min === 'number' && min > 1000 ? fmtInt(min) : min}</text>
      </svg>
    </div>
  );
}

export default function MAStateDetailPage() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [payers, setPayers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.maState(code.toUpperCase()).then(setD).catch(e => setErr(e?.response?.data?.detail || 'Not found')),
      api.maStatePayers(code.toUpperCase(), { limit: 15 }).then(setPayers).catch((e) => reportError('MAStateDetail.payers', e, { code })),
    ]).finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="min-h-screen"><Navbar/><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading state MA market…</div></main></div>;
  if (err || !d) return <div className="min-h-screen"><Navbar/><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">State not in MA index</h1><p className="mt-3 text-[#7A857F]">{err}</p></main></div>;

  const opp = d.opportunity || {};

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[
          { label: 'Home', to: '/' },
          { label: 'Managed Care', to: '/managed-care' },
          { label: d.state_name }
        ]} />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <Activity size={12} className="text-[#4A7C5E]"/>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Managed Care · {d.state_code}</span>
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">{d.state_name}</h1>
            <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider"
                 style={{ background: `${opp.color}1A`, color: opp.color }}>
              {opp.label} · score {opp.score ?? '—'}
            </div>
            <p className="mt-3 text-[13.5px] text-[#3A4A44] max-w-2xl leading-relaxed">{opp.description}</p>
          </div>
          <div className="lg:col-span-4 self-start">
            <div className="rounded-xl bg-white border border-[#E5DCC4] p-5">
              <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">State MA penetration</div>
              <div className="mt-1 font-display text-4xl text-[#1A2622]">{fmtPct(d.latest_penetration, 1)}</div>
              <div className="mt-2 text-[12px] text-[#5A6660]">National rank <span className="font-semibold text-[#1A2622]">#{d.national_rank ?? '—'}</span> · National avg {fmtPct(d.national_avg_penetration, 1)}</div>
              {d.vs_national_delta != null && (
                <div className={`mt-2 inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold ${d.vs_national_delta >= 0 ? 'bg-[#FBE6E0] text-[#C75D5D]' : 'bg-[#E8F1EA] text-[#4A7C5E]'}`}>
                  {d.vs_national_delta > 0 ? '+' : ''}{d.vs_national_delta} pp vs national
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat value={fmtInt(d.latest_ma_beneficiaries)} label="MA beneficiaries (rolled up)" />
          <Stat value={fmtInt(d.latest_total_beneficiaries)} label="Medicare eligibles" />
          <Stat value={d.counties_indexed ?? '—'} label="Counties indexed" />
          <Stat value={fmtInt(d.snf_count)} label="SNFs in state" />
        </section>

        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
            <LineChart trend={d.trend} accessor="ma_beneficiaries" color={opp.color || '#4A7C5E'} label={`MA beneficiaries · ${d.years_span || ''}`} />
            <div className="mt-3 text-[12px] text-[#5A6660]">
              {d.trend_pct_change != null && (
                <>YoY change: <span className={`font-semibold ${d.trend_pct_change >= 0 ? 'text-[#4A7C5E]' : 'text-[#C75D5D]'}`}>{d.trend_pct_change > 0 ? '+' : ''}{d.trend_pct_change}%</span></>
              )}
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
            <LineChart trend={d.trend} accessor="snf_utilization_pct" color="#D4A85C" label="SNF utilization rate (MA enrollees)" />
            <div className="mt-3 text-[12px] text-[#5A6660]">Share of MA beneficiaries with a SNF stay each year (CMS MA GV PUF).</div>
          </div>
        </section>

        <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
              <TrendingUp size={14} className="text-[#C75D5D]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Most saturated counties</h2>
            </div>
            <ul>
              {(d.counties_top_penetration || []).map((c, i) => (
                <li key={`hp-${c.fips || i}`} className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] first:border-t-0">
                  <div>
                    <div className="text-[13px] font-medium text-[#1A2622]">{c.county_name}</div>
                    <div className="text-[10.5px] text-[#7A857F]">{fmtInt(c.enrolled)} of {fmtInt(c.eligibles)} eligibles</div>
                  </div>
                  <span className="text-[13px] font-semibold text-[#C75D5D] tabular-nums">{fmtPct(c.penetration_pct, 1)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
              <MapPin size={14} className="text-[#4A7C5E]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Highest opportunity counties</h2>
            </div>
            <ul>
              {(d.counties_top_opportunity || []).map((c, i) => (
                <li key={`op-${c.fips || i}`} className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] first:border-t-0">
                  <div>
                    <div className="text-[13px] font-medium text-[#1A2622]">{c.county_name}</div>
                    <div className="text-[10.5px] text-[#7A857F]">{fmtInt(c.eligibles)} eligibles · {fmtInt(c.enrolled)} enrolled</div>
                  </div>
                  <span className="text-[13px] font-semibold text-[#4A7C5E] tabular-nums">{fmtPct(c.penetration_pct, 1)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {(payers?.items || []).length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden" data-testid="state-payers-section">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
              <Building2 size={14} className="text-[#4A7C5E]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Top MA payers in {d.state_name}</h2>
              <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">
                {fmtInt(payers.state_total_enrolled)} total MA enrolled
              </span>
            </div>
            <ul>
              {payers.items.map((p, i) => (
                <li key={p.organization_name_norm} className="px-4 py-2.5 flex items-center gap-3 border-t border-[#F0E7D2] first:border-t-0">
                  <span className="w-5 text-[11px] text-[#7A857F]">{i + 1}</span>
                  <Link to={`/managed-care/payer/${encodeURIComponent(p.organization_name_norm)}`} className="flex-1 min-w-0 text-[12.5px] text-[#1A2622] font-medium hover:text-[#2D5F4F] truncate">
                    {p.organization_name_display}
                  </Link>
                  <span className="text-[10.5px] text-[#7A857F] hidden md:inline">{p.counties_served} counties · {p.contract_count} contracts</span>
                  <span className="text-[12.5px] font-semibold tabular-nums w-20 text-right">{fmtInt(p.total_enrolled)}</span>
                  <span className="text-[12.5px] font-semibold text-[#4A7C5E] tabular-nums w-14 text-right">{p.state_share_pct != null ? p.state_share_pct.toFixed(1) + '%' : '—'}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(d.top_facilities || []).length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
              <Building2 size={14} className="text-[#4A7C5E]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Top-rated SNFs in {d.state_name}</h2>
              <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">{fmtInt(d.snf_count)} total</span>
            </div>
            <ul>
              {d.top_facilities.map((f) => (
                <li key={f.ccn}>
                  <Link to={`/facilities/${f.ccn}`} className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] first:border-t-0 hover:bg-[#FBF6EC]">
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-[#1A2622] truncate">{f.name}</div>
                      <div className="text-[10.5px] text-[#7A857F]">{f.city}, {d.state_code} · {f.county || ''} County · {f.beds || '—'} beds</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[12px] font-semibold text-[#1A2622]">{f.rating ?? '—'}★</span>
                      <ArrowUpRight size={13} className="text-[#A8B0AA]"/>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA]">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>Source: {d.source}. Secondary metrics from {d.secondary_source}. Penetration is computed by rolling up county-level CMS-published MA enrolled / Medicare eligibles. Trend chart spans {d.years_span || 'CMS MA GV PUF'}.</span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
